import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-hot-toast";
import {
  getOneDeskHotels,
  updateHotel,
  getUploadImageUrl,
  imageOriginal,
} from "@repo/api-client";
import type { HotelListItem, UpdateHotelRequest } from "@repo/types";

export const useHotelList = () => {
  const [hotels, setHotels] = useState<HotelListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedHotel, setSelectedHotel] = useState<HotelListItem | null>(
    null,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editForm, setEditForm] = useState<Partial<UpdateHotelRequest>>({});
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);

  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [status, setStatus] = useState<string>("all");
  const [salesmanCode, setSalesmanCode] = useState("");

  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  // Ref to track if filter change triggered the page reset (to avoid double fetch)
  const isFilterResetRef = useRef(false);

  const fetchHotels = useCallback(
    async (pageNum: number) => {
      try {
        setLoading(true);
        const response = await getOneDeskHotels({
          PageNumber: pageNum,
          PageSize: pageSize,
          Search: searchQuery || null,
          City: city || null,
          State: state || null,
          SalesmanCode: salesmanCode || null,
          IsActive: status === "all" ? null : status === "active",
        });
        if (response.Success) {
          setHotels(response.Data || []);
          if ((response as any).TotalCount) {
            setTotalCount((response as any).TotalCount);
          }
        }
      } catch (error) {
        console.error("Error fetching hotels:", error);
        toast.error("Failed to fetch hotels");
      } finally {
        setLoading(false);
      }
    },
    [searchQuery, city, state, salesmanCode, status, pageSize],
  );

  // Debounced effect for filter changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (page === 1) {
        // Already on page 1 — fetch directly
        fetchHotels(1);
      } else {
        // Mark that this page-reset is from a filter change, not a user page nav
        isFilterResetRef.current = true;
        setPage(1);
      }
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, city, state, salesmanCode, status]);

  // Effect for page navigation
  useEffect(() => {
    if (isFilterResetRef.current) {
      // This page change was triggered by a filter reset — fetchHotels will be
      // called by the filter effect on next render, so skip here to avoid double call
      isFilterResetRef.current = false;
      fetchHotels(page);
      return;
    }
    fetchHotels(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Cleanup blob preview URL on unmount / change
  useEffect(() => {
    return () => {
      if (logoPreview && logoPreview.startsWith("blob:")) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  const mapHotelToForm = useCallback(
    (hotel: HotelListItem): Partial<UpdateHotelRequest> => ({
      HotelName: hotel.HotelName,
      Address: hotel.Address || "",
      City: hotel.City || "",
      State: hotel.State || "",
      Country: hotel.Country || "",
      PostalCode: String(hotel.PostalCode || ""),
      Phone: hotel.Phone || "",
      Email: hotel.Email || "",
      Website: hotel.Website || "",
      TaxNumber: hotel.TaxNumber || "",
      IsActive: hotel.IsActive ?? true,
      CheckInTime: hotel.CheckInTime || "12:00 PM",
      CheckOutTime: hotel.CheckOutTime || "11:00 AM",
      GST: hotel.GST || 0,
      FoodGST: hotel.FoodGST || 0,
      ServiceCharge: hotel.ServiceCharge || 0,
      CurrencySymbol: hotel.CurrencySymbol || "₹",
      LogoUrl: hotel.LogoUrl || "",
    }),
    [],
  );

  const handleViewDetails = useCallback(
    (hotel: HotelListItem) => {
      setSelectedHotel(hotel);
      setEditForm(mapHotelToForm(hotel));
      setLogoPreview(hotel.LogoUrl ? imageOriginal(hotel.LogoUrl) : null);
      setIsEditMode(false);
      setIsModalOpen(true);
    },
    [mapHotelToForm],
  );

  const handleEdit = useCallback(
    (hotel: HotelListItem) => {
      setSelectedHotel(hotel);
      setEditForm(mapHotelToForm(hotel));
      setLogoPreview(hotel.LogoUrl ? imageOriginal(hotel.LogoUrl) : null);
      setIsEditMode(true);
      setIsModalOpen(true);
    },
    [mapHotelToForm],
  );

  const handleLogoChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (file.size > 1 * 1024 * 1024) {
        toast.error("Image must be less than 1MB");
        return;
      }

      if (!["image/jpeg", "image/png"].includes(file.type)) {
        toast.error("Only JPG and PNG allowed");
        return;
      }

      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);

      try {
        setIsUploadingLogo(true);
        const res = await getUploadImageUrl(file);
        if (res.success && res.data?.id) {
          setEditForm((prev: any) => ({ ...prev, LogoUrl: res.data.id }));
          toast.success("Logo uploaded");
        } else {
          toast.error("Upload failed");
          setEditForm((prev) => {
            setLogoPreview(prev.LogoUrl ? imageOriginal(prev.LogoUrl) : null);
            return prev;
          });
        }
      } catch (err) {
        toast.error("Error uploading logo");
      } finally {
        setIsUploadingLogo(false);
      }
    },
    [],
  );

  const handleSave = useCallback(async () => {
    if (!selectedHotel) return;
    try {
      setIsUpdating(true);
      const response = await updateHotel({
        ...editForm,
        HotelID: selectedHotel.HotelID,
      } as any);
      if (response.Success) {
        toast.success("Hotel updated successfully");
        setIsModalOpen(false);
        fetchHotels(page);
      } else {
        toast.error(response.Message || "Failed to update hotel");
      }
    } catch (error) {
      console.error("Error updating hotel:", error);
      toast.error("An unexpected error occurred");
    } finally {
      setIsUpdating(false);
    }
  }, [selectedHotel, editForm, fetchHotels, page]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleNextPage = useCallback(() => {
    if (totalCount > 0) {
      if (page * pageSize < totalCount) {
        setPage((p) => p + 1);
      }
    } else if (hotels.length === pageSize) {
      setPage((p) => p + 1);
    }
  }, [totalCount, page, pageSize, hotels.length]);

  const handlePrevPage = useCallback(() => {
    if (page > 1) {
      setPage((p) => p - 1);
    }
  }, [page]);

  return {
    loading,
    searchQuery,
    setSearchQuery,
    city,
    setCity,
    state,
    setState,
    status,
    setStatus,
    salesmanCode,
    setSalesmanCode,
    filteredHotels: hotels,
    page,
    setPage,
    pageSize,
    totalCount,
    handleNextPage,
    handlePrevPage,
    selectedHotel,
    isModalOpen,
    isEditMode,
    isUpdating,
    editForm,
    setEditForm,
    logoPreview,
    isUploadingLogo,
    handleViewDetails,
    handleEdit,
    handleLogoChange,
    handleSave,
    handleCloseModal,
    fetchHotels,
  };
};
